import React, { useCallback, useEffect, useState } from "react";

import { Briefcase, Loader2, Save } from "lucide-react";

import Button from "../../../Components/Button";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchPortfolioNeedsAttentionConfig,
  updatePortfolioNeedsAttentionConfig,
  type PortfolioNeedsAttentionSettings,
} from "../../../Store/Reducers/VendorAdminSlice";
import { toast } from "../../../Utils/Helper/toast";
import NeedsAttentionCriteriaForm from "../NeedsAttentionCriteriaForm";

const PORTFOLIO_NEEDS_ATTENTION_KEY = "portfolio_needs_attention";

export type { PortfolioNeedsAttentionSettings };

export const DEFAULT_PORTFOLIO_NEEDS_ATTENTION: PortfolioNeedsAttentionSettings =
  {
    health_threshold: 65,
    renewal_days: 90,
    include_triage: false,
    include_defend: false,
    include_bh_concern: true,
    include_unscored: false,
    include_unmapped: false,
    include_unknown_al: false,
  };

export const normalizePortfolioNeedsAttention = (
  raw?: Partial<PortfolioNeedsAttentionSettings> | null
): PortfolioNeedsAttentionSettings => {
  const health = Number(raw?.health_threshold);
  const renewal = Number(raw?.renewal_days);
  return {
    health_threshold: Number.isFinite(health)
      ? Math.max(0, Math.min(100, Math.round(health)))
      : DEFAULT_PORTFOLIO_NEEDS_ATTENTION.health_threshold,
    renewal_days: Number.isFinite(renewal)
      ? Math.max(0, Math.round(renewal))
      : DEFAULT_PORTFOLIO_NEEDS_ATTENTION.renewal_days,
    include_triage: Boolean(raw?.include_triage),
    include_defend: Boolean(raw?.include_defend),
    include_bh_concern:
      raw?.include_bh_concern ??
      DEFAULT_PORTFOLIO_NEEDS_ATTENTION.include_bh_concern,
    include_unscored: Boolean(raw?.include_unscored),
    include_unmapped: Boolean(raw?.include_unmapped),
    include_unknown_al: Boolean(raw?.include_unknown_al),
  };
};

const PortfolioNeedsAttentionOrgSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    portfolioNeedsAttentionValue,
    portfolioNeedsAttentionLoading,
    portfolioNeedsAttentionUpdateLoading,
  } = useAppSelector(state => state.vendorAdmin);

  const [settings, setSettings] = useState<PortfolioNeedsAttentionSettings>(
    DEFAULT_PORTFOLIO_NEEDS_ATTENTION
  );

  const load = useCallback(async () => {
    try {
      const value = await dispatch(
        fetchPortfolioNeedsAttentionConfig()
      ).unwrap();
      setSettings(normalizePortfolioNeedsAttention(value));
    } catch {
      setSettings(DEFAULT_PORTFOLIO_NEEDS_ATTENTION);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (portfolioNeedsAttentionValue) {
      setSettings(
        normalizePortfolioNeedsAttention(portfolioNeedsAttentionValue)
      );
    }
  }, [portfolioNeedsAttentionValue]);

  const handleSave = async () => {
    const normalized = normalizePortfolioNeedsAttention(settings);
    try {
      const value = await dispatch(
        updatePortfolioNeedsAttentionConfig({
          key: PORTFOLIO_NEEDS_ATTENTION_KEY,
          value: normalized,
        })
      ).unwrap();
      setSettings(normalizePortfolioNeedsAttention(value));
      toast.success("Needs Attention settings saved");
    } catch (error: unknown) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save Needs Attention settings"
      );
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_PORTFOLIO_NEEDS_ATTENTION);
  };

  return (
    <div className="settings-section rounded-lg border border-gray-200 bg-white shadow">
      <div className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-purple-600" />
          <h2 className="fs-18 fw-600 mb-0 text-gray-900">
            Portfolio — Needs Attention
          </h2>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          Accounts matching <strong>any</strong> of the active criteria are
          flagged as Needs Attention on My Accounts.
        </p>

        {portfolioNeedsAttentionLoading ? (
          <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
            Loading…
          </div>
        ) : (
          <div className="space-y-5">
            <NeedsAttentionCriteriaForm
              value={settings}
              onChange={patch => setSettings(prev => ({ ...prev, ...patch }))}
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={handleReset}
                disabled={portfolioNeedsAttentionUpdateLoading}
              >
                Reset to defaults
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={portfolioNeedsAttentionUpdateLoading}
                loading={portfolioNeedsAttentionUpdateLoading}
                icon={<Save className="h-4 w-4" />}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioNeedsAttentionOrgSettings;
